import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { CardBrandIcon } from '../card-brand-icon';

export interface PaymentMethodCardProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  brand: string;
  last4: string;
  /** Optional secondary line, e.g. expiry. */
  meta?: ReactNode;
  selected?: boolean;
  /** Slot at the right edge — typically the delete button. */
  trailing?: ReactNode;
}

/**
 * Selectable saved-payment-method tile. Designed to read as a credit card
 * (brand mark + masked PAN) rather than a generic list row. Owns its
 * focus / hover / selected states so the consumer just toggles `selected`.
 */
export const PaymentMethodCard = forwardRef<HTMLButtonElement, PaymentMethodCardProps>(
  ({ brand, last4, meta, selected = false, trailing, className, disabled, ...rest }, ref) => {
    return (
      <div className="relative">
        <button
          ref={ref}
          type="button"
          aria-pressed={selected}
          disabled={disabled}
          className={cn(
            'group relative w-full overflow-hidden text-left',
            'flex items-center gap-3',
            'rounded-card border bg-surface',
            'py-3 pl-4',
            trailing ? 'pr-12' : 'pr-3',
            'transition-[border-color,background-color,box-shadow,transform] duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2',
            selected
              ? 'border-accent shadow-card bg-gradient-to-br from-accent/[0.06] to-accent/[0.02]'
              : 'border-border hover:border-accent/40 hover:shadow-card',
            disabled && 'pointer-events-none opacity-60',
            className,
          )}
          {...rest}
        >
          {/* selected accent stripe */}
          <span
            aria-hidden="true"
            className={cn(
              'absolute left-0 top-0 bottom-0 w-1 transition-colors',
              selected ? 'bg-gradient-to-b from-accent to-accent-dark' : 'bg-transparent',
            )}
          />
          <CardBrandIcon brand={brand} size={36} />
          <div className="flex flex-col min-w-0">
            <span className="flex items-baseline gap-2 text-sm">
              <span className="font-semibold capitalize text-primary">{brand}</span>
              <span className="font-mono text-muted text-xs tracking-wider">•••• {last4}</span>
            </span>
            {meta && <span className="text-xs text-muted leading-none mt-1">{meta}</span>}
          </div>
        </button>
        {trailing && <span className="absolute right-2 top-1/2 -translate-y-1/2">{trailing}</span>}
      </div>
    );
  },
);
PaymentMethodCard.displayName = 'PaymentMethodCard';
